import type { operations, paths } from "@airange/generated-contracts/sso-center";
import { ApiError } from "@airange/shared/api";
import { normalizeAppLocalUrlPath, toAppLocalUrlPath } from "@airange/shared/app-public-path";
import { normalizeCenterOpenApiBaseUrl } from "@airange/shared/center-openapi-transport";
import {
  PUBLIC_APPLICATION_DEPLOYMENT_MODES,
  type PublicApplicationDeploymentMode,
} from "@airange/shared/public-deployment-modes";
import createOpenApiClient from "openapi-fetch";
import { SSO_API_BASE_URL } from "./env";
import { unwrapOpenApiResult } from "./openapi-result";
import { sanitizeRedirectPath } from "./sso-redirect";

type GeneratedIdentityApplicationPublicConfigRead =
  operations["get_identity_application_public_config_api_identity_public_applications__app_key__get"]["responses"][200]["content"]["application/json"];

type IdentityApplicationPublicConfigRead = GeneratedIdentityApplicationPublicConfigRead;
type AdminPublicAccessSettingsRead = NonNullable<
  IdentityApplicationPublicConfigRead["admin_access"]
>;

export type AuthDeploymentProfile = AdminPublicAccessSettingsRead["auth_deployment_profile"];
export type PublicApplicationAuthMode = AdminPublicAccessSettingsRead["auth_mode"];

export type AdminRuntimeAccessConfig = {
  adminBaseUrl: string;
  authMode: PublicApplicationAuthMode;
  authDeploymentProfile: AuthDeploymentProfile;
  defaultRedirectPath: string;
  secureContextRequired: boolean;
  supportedDeploymentModes: PublicApplicationDeploymentMode[];
};

const ADMIN_APPLICATION_KEY = "airange-admin";
const ADMIN_RUNTIME_CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;

export function createAdminRuntimeConfigError(
  code: string,
  message: string,
  details?: Record<string, unknown>,
) {
  return new ApiError(500, {
    code,
    details,
    message,
  });
}

function requireNonEmptyString(value: unknown, fieldName: string) {
  if (typeof value !== "string" || !value.trim()) {
    throw createAdminRuntimeConfigError(
      "admin.access.required_field_missing",
      `Admin runtime access config field ${fieldName} is required.`,
      { fieldName },
    );
  }
  return value.trim();
}

function normalizeAdminBaseUrl(value: unknown) {
  const rawBaseUrl = requireNonEmptyString(value, "admin_access.admin_base_url");
  let parsed: URL;
  try {
    parsed = new URL(rawBaseUrl);
  } catch {
    throw createAdminRuntimeConfigError(
      "admin.access.admin_base_url_invalid",
      "Admin runtime access config field admin_access.admin_base_url must be an absolute URL.",
      { value: rawBaseUrl },
    );
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw createAdminRuntimeConfigError(
      "admin.access.admin_base_url_invalid",
      "Admin runtime access config field admin_access.admin_base_url must use http or https.",
      { value: rawBaseUrl },
    );
  }
  if (typeof window !== "undefined" && parsed.origin !== window.location.origin) {
    throw createAdminRuntimeConfigError(
      "admin.access.admin_base_url_mismatch",
      "Admin runtime access config field admin_access.admin_base_url does not match the current admin origin.",
      {
        configuredOrigin: parsed.origin,
        currentOrigin: window.location.origin,
      },
    );
  }
  return parsed.toString().replace(/\/+$/, "");
}

function normalizeRedirectTarget(rawValue: unknown, fallback: string | null, fieldName: string) {
  if (rawValue === null || rawValue === undefined) {
    if (fallback !== null) {
      return fallback;
    }
    throw createAdminRuntimeConfigError(
      "admin.access.redirect_target_required",
      `Admin runtime access config field ${fieldName} is required.`,
      { fieldName },
    );
  }
  if (typeof rawValue !== "string") {
    if (fallback !== null) {
      return fallback;
    }
    throw createAdminRuntimeConfigError(
      "admin.access.redirect_target_invalid_type",
      `Admin runtime access config field ${fieldName} must be a string.`,
      { fieldName, value: rawValue },
    );
  }
  const value = rawValue.trim();
  if (!value) {
    if (fallback !== null) {
      return fallback;
    }
    throw createAdminRuntimeConfigError(
      "admin.access.redirect_target_required",
      `Admin runtime access config field ${fieldName} is required.`,
      { fieldName },
    );
  }
  if (value.startsWith("/") && !value.startsWith("//")) {
    return value;
  }
  if (typeof window !== "undefined") {
    try {
      const parsed = new URL(value, window.location.origin);
      if (parsed.origin === window.location.origin) {
        return `${parsed.pathname}${parsed.search}${parsed.hash}` || fallback || "/";
      }
    } catch {
      // Fall through to the strict failure below.
    }
  }
  if (fallback !== null) {
    return fallback;
  }
  throw createAdminRuntimeConfigError(
    "admin.access.redirect_target_invalid",
    `Admin runtime access config field ${fieldName} is not a same-origin redirect target.`,
    { fieldName, value },
  );
}

function requireSafeRedirectTarget(rawValue: unknown, fieldName: string) {
  return normalizeRedirectTarget(rawValue, null, fieldName);
}

function normalizeAuthCapabilities(
  adminAccess: AdminPublicAccessSettingsRead,
  authDeploymentProfile: AuthDeploymentProfile,
): Pick<
  AdminRuntimeAccessConfig,
  "authMode" | "secureContextRequired" | "supportedDeploymentModes"
> {
  const authMode = adminAccess.auth_mode;
  if (authMode !== "bff") {
    throw createAdminRuntimeConfigError(
      "admin.access.auth_capability_mismatch",
      `Admin auth_mode must be bff for ${authDeploymentProfile}.`,
      { authDeploymentProfile, authMode },
    );
  }
  if (adminAccess.secure_context_required !== false) {
    throw createAdminRuntimeConfigError(
      "admin.access.auth_capability_mismatch",
      "Admin secure_context_required must be false for bff.",
      { authMode, secureContextRequired: adminAccess.secure_context_required },
    );
  }
  const expectedModes = PUBLIC_APPLICATION_DEPLOYMENT_MODES;
  if (
    !Array.isArray(adminAccess.supported_deployment_modes) ||
    adminAccess.supported_deployment_modes.length !== expectedModes.length ||
    adminAccess.supported_deployment_modes.some((mode, index) => mode !== expectedModes[index])
  ) {
    throw createAdminRuntimeConfigError(
      "admin.access.auth_capability_mismatch",
      "Admin supported_deployment_modes must equal http_domain, http_ip, https_ip, https_domain.",
      { supportedDeploymentModes: adminAccess.supported_deployment_modes },
    );
  }
  return {
    authMode: "bff",
    secureContextRequired: false,
    supportedDeploymentModes: [...expectedModes],
  };
}

function mapAdminRuntimeAccessConfig(
  payload: IdentityApplicationPublicConfigRead,
): AdminRuntimeAccessConfig {
  if (payload.status !== "active") {
    const status = payload.status;
    const statusLabel =
      typeof status === "string" && status.trim()
        ? status.trim()
        : status === null || status === undefined
          ? "missing"
          : "invalid";
    throw createAdminRuntimeConfigError(
      "admin.access.application_inactive",
      `Admin application status is ${statusLabel}.`,
      { status },
    );
  }
  const adminAccess = payload.admin_access;
  if (!adminAccess) {
    throw createAdminRuntimeConfigError(
      "admin.access.public_subset_missing",
      "Admin public application config must include admin_access.",
    );
  }
  const authDeploymentProfile = adminAccess.auth_deployment_profile;
  const authCapabilities = normalizeAuthCapabilities(adminAccess, authDeploymentProfile);
  return {
    adminBaseUrl: normalizeAdminBaseUrl(adminAccess.admin_base_url),
    ...authCapabilities,
    authDeploymentProfile,
    defaultRedirectPath: requireSafeRedirectTarget(
      adminAccess.default_redirect_path,
      "admin_access.default_redirect_path",
    ),
  };
}

async function requestAdminRuntimePublicConfig(fetchImpl: typeof fetch) {
  const client = createOpenApiClient<paths>({
    baseUrl: normalizeCenterOpenApiBaseUrl(SSO_API_BASE_URL, { mode: "service-root" }),
    fetch: async (request: Request): Promise<Response> => {
      return fetchImpl(request.url, {
        credentials: "include",
        headers: request.headers,
        method: request.method,
      });
    },
  });
  return await unwrapOpenApiResult(
    client.GET("/api/identity/public-applications/{app_key}", {
      params: {
        path: {
          app_key: ADMIN_APPLICATION_KEY,
        },
      },
    }),
    {
      emptyResponseError: {
        code: "admin.access.public_config_empty",
        message: "Admin public application config response is empty.",
      },
      read: mapAdminRuntimeAccessConfig,
    },
  );
}

type AdminRuntimeAccessConfigCacheState = {
  cachedAdminRuntimeAccessConfig: {
    expiresAt: number;
    value: AdminRuntimeAccessConfig;
  } | null;
  adminRuntimeAccessConfigPromise: Promise<AdminRuntimeAccessConfig> | null;
};

const adminRuntimeAccessConfigCacheByFetch = new WeakMap<
  typeof fetch,
  AdminRuntimeAccessConfigCacheState
>();

function readAdminRuntimeAccessConfigCache(fetchImpl: typeof fetch) {
  let cache = adminRuntimeAccessConfigCacheByFetch.get(fetchImpl);
  if (!cache) {
    cache = {
      cachedAdminRuntimeAccessConfig: null,
      adminRuntimeAccessConfigPromise: null,
    };
    adminRuntimeAccessConfigCacheByFetch.set(fetchImpl, cache);
  }
  return cache;
}

function resetAdminRuntimeAccessConfig(cache: AdminRuntimeAccessConfigCacheState) {
  cache.cachedAdminRuntimeAccessConfig = null;
  cache.adminRuntimeAccessConfigPromise = null;
}

export async function resolveAdminRuntimeAccessConfig(
  fetchImpl: typeof fetch = fetch,
  forceRefresh = false,
): Promise<AdminRuntimeAccessConfig> {
  const cache = readAdminRuntimeAccessConfigCache(fetchImpl);
  if (
    !forceRefresh &&
    cache.cachedAdminRuntimeAccessConfig &&
    cache.cachedAdminRuntimeAccessConfig.expiresAt > Date.now()
  ) {
    return cache.cachedAdminRuntimeAccessConfig.value;
  }
  if (cache.adminRuntimeAccessConfigPromise) {
    return await cache.adminRuntimeAccessConfigPromise;
  }
  cache.adminRuntimeAccessConfigPromise = (async () => {
    const value = await requestAdminRuntimePublicConfig(fetchImpl);
    cache.cachedAdminRuntimeAccessConfig = {
      expiresAt: Date.now() + ADMIN_RUNTIME_CONFIG_CACHE_TTL_MS,
      value,
    };
    return value;
  })();
  try {
    return await cache.adminRuntimeAccessConfigPromise;
  } catch (error) {
    resetAdminRuntimeAccessConfig(cache);
    throw error;
  } finally {
    cache.adminRuntimeAccessConfigPromise = null;
  }
}

function adminBrowserPublicPath(config: AdminRuntimeAccessConfig) {
  const pathname = new URL(config.adminBaseUrl).pathname.replace(/\/+$/, "");
  return pathname === "/" ? "" : pathname;
}

export function sanitizeAdminRuntimeRedirectPath(
  config: AdminRuntimeAccessConfig,
  rawValue: string | null | undefined,
) {
  const sanitizedPath = sanitizeRedirectPath(rawValue, config.defaultRedirectPath);
  const publicPath = adminBrowserPublicPath(config);
  const pathname = new URL(sanitizedPath, config.adminBaseUrl).pathname;
  if (publicPath && (pathname === publicPath || pathname.startsWith(`${publicPath}/`))) {
    return toAppLocalUrlPath(sanitizedPath, publicPath);
  }
  return normalizeAppLocalUrlPath(sanitizedPath, publicPath);
}
